import React, { useMemo } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { 
  formatYAxisValue, 
  formatHorizontalBarLabel, 
  formatCurrency 
} from './DrillDown/formatters';
import { DonutChartSkeleton } from './Skeletons/ChartSkeletons';
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme
} from '@mui/material';

interface DonutChartWithTableProps {
  data: any[];
  loading?: boolean;
  title: string;
  valueKey?: string; // Chave para o valor (padrão: 'value')
  nameKey?: string; // Chave para o nome (padrão: 'id')
  onItemClick?: (item: any) => void;
  height?: number;
  showTotal?: boolean; // Se deve mostrar total nos valores
  formatValue?: (value: number) => string; // Função customizada para formatar valores
  spedFiscalId?: number; // Para drill-down se necessário
}

const DonutChartWithTable: React.FC<DonutChartWithTableProps> = ({
  data,
  loading = false,
  title,
  valueKey = 'value',
  nameKey = 'id',
  onItemClick,
  height = 430,
  showTotal = true,
  formatValue = formatCurrency,
  spedFiscalId
}) => {
  const theme = useTheme();

    // Processar dados para incluir apenas TOP 10
  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    // Ordenar dados por valor em ordem decrescente
    const sortedData = [...data].sort((a, b) =>
      (b[valueKey] as number) - (a[valueKey] as number)
    );

    // Top 10 para o gráfico (com labels truncados)
    const top10 = sortedData.slice(0, 10).map((item) => ({
      ...item,
      idCompleto: item[nameKey] as string, // Nome completo para tooltip
      [nameKey]: formatHorizontalBarLabel(item[nameKey] as string, 25) // Nome truncado para display
    }));

    return top10;
  }, [data, valueKey, nameKey]);

  const handlePieClick = (clickData: any) => {
    if (onItemClick) {
      // Encontrar o item original usando o nome truncado
      const truncatedName = clickData.id;
      const clickedItem = chartData.find(item => 
        item[nameKey] === truncatedName
      );
      
      if (clickedItem) {
        // Buscar o item original não processado
        const originalItem = data.find(item => 
          item[nameKey] === clickedItem.idCompleto
        );
        if (originalItem) {
          onItemClick(originalItem);
        }
      }
    }
  };

  if (loading) {
    return (
      <Card sx={{ 
        p: 2, 
        mt: 0, 
        height: height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ 
          p: 0, 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          '&:last-child': { pb: 0 }
        }}>
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ color: theme.palette.text.secondary, mb: 2, flexShrink: 0 }}
          >
            {title}
          </Typography>
          <DonutChartSkeleton height={height - 100} showTitle={false} />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card sx={{ 
        p: 2, 
        mt: 0, 
        height: height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ 
          p: 0, 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          '&:last-child': { pb: 0 }
        }}>
          <Typography
            variant="subtitle1"
            align="center"
            sx={{ color: theme.palette.text.secondary, mb: 2, flexShrink: 0 }}
          >
            {title}
          </Typography>
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: height - 100,
              color: 'text.secondary'
            }}
          >
            <Typography variant="body1">
              📊 Nenhum dado disponível
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Calcular altura para o gráfico
  const chartHeight = 350; // Altura otimizada para donut sem tabela

  return (
    <Card sx={{
      p: 2,
      mt: 0,
      height: height || (chartHeight), // Ajustado para altura sem tabela
      display: 'flex',
      flexDirection: 'column',
      overflow: 'visible'
    }}>
      <CardContent sx={{ 
        p: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        '&:last-child': { pb: 0 }
      }}>
        <Typography
          variant="subtitle1"
          align="center"
          sx={{ color: theme.palette.text.secondary, mb: 2, flexShrink: 0 }}
        >
          {title}
        </Typography>

        {/* Gráfico Donut TOP 10 */}
        <Box sx={{
          height: chartHeight,
          flexShrink: 0,
          overflow: 'visible',
          position: 'relative',
          zIndex: 10,
          mb: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {chartData.length > 0 ? (
            <ResponsivePie
              data={chartData.map(item => ({
                id: item[nameKey] as string,
                value: item[valueKey] as number,
                originalData: item
              }))}
              onClick={onItemClick ? handlePieClick : undefined}
              valueFormat={(value) => formatYAxisValue(value)}
              margin={{ top: 20, right: 40, bottom: 20, left: 40 }}
              innerRadius={0.55}
              padAngle={1.5}
              cornerRadius={5}
              activeOuterRadiusOffset={12}
              colors={{ scheme: 'pastel1' }}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
              arcLinkLabelsSkipAngle={12}
              arcLinkLabelsTextColor={theme.palette.text.primary}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: 'color' }}
              arcLabelsSkipAngle={15}
              arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
              tooltip={({ datum }) => {
                const originalItem = (datum.data as any).originalData;
                const originalValue = originalItem[valueKey] as number;
                const originalName = originalItem.idCompleto || originalItem[nameKey] as string;

                return (
                  <Box
                    sx={{
                      background: theme.palette.background.paper,
  
                      p: 2,
                      minWidth: 250,
                      maxWidth: 350,
                      border: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                      📊 {originalName}
                    </Typography>
                    <Typography variant="h6" color="primary.main" sx={{ mb: 1 }}>
                      {formatValue(originalValue)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {((originalValue / data.reduce((sum, item) => sum + (item[valueKey] as number), 0)) * 100).toFixed(1)}% do total
                    </Typography>
                    {onItemClick && (
                      <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 1, fontWeight: 500 }}>
                        💡 Clique para análise detalhada
                      </Typography>
                    )}
                  </Box>
                );
              }}
              animate={true}
              motionConfig="gentle"
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              📊 Nenhum dado disponível para o gráfico
            </Typography>
          )}
        </Box>

      </CardContent>
    </Card>
  );
};

export default DonutChartWithTable;
